"use client";

import { api } from "convex/_generated/api";
import { useMutation } from "convex/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "app_feedback_widget_minimized";

type FeedbackCategory = "feedback" | "feature_request";

export function GlobalFeedbackWidget() {
  const reduceMotion = useReducedMotion();
  const submit = useMutation(api.feedback.submitAppFeedback);
  const [minimized, setMinimized] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved !== "0";
  });
  const [rating, setRating] = useState<number | null>(null);
  const [category, setCategory] = useState<FeedbackCategory>("feedback");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const canSubmit = useMemo(
    () => rating !== null && message.trim().length > 0 && !busy,
    [rating, message, busy],
  );

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, minimized ? "1" : "0");
  }, [minimized]);

  return (
    <div className="pointer-events-none mt-8 flex w-full justify-end">
      <AnimatePresence mode="wait">
        {minimized ? (
          <motion.button
            key="minimized"
            type="button"
            className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-lg"
            onClick={() => setMinimized(false)}
            initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 10, scale: 0.96 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 360, damping: 28 }
            }
          >
            <span aria-hidden>💬</span>
            How can we improve?
          </motion.button>
        ) : (
          <motion.div
            key="expanded"
            className="pointer-events-auto w-[min(92vw,360px)] rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-2xl"
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 10, scale: 0.97 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 320, damping: 26 }
            }
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Feedback
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  Rate darsi.ma and share your ideas
                </p>
              </div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                onClick={() => setMinimized(true)}
              >
                Minimize
              </button>
            </div>

            {submitted ? (
              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/10 p-3 text-sm text-foreground">
                Thanks for your feedback. We appreciate your support.
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <p className="text-xs text-muted-foreground">Rating</p>
                  <div className="mt-2 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={cn(
                          "rounded-lg px-2 py-1 text-lg transition-colors",
                          n <= (rating ?? 0)
                            ? "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300"
                            : "bg-muted text-muted-foreground",
                        )}
                        onClick={() => setRating(n)}
                        aria-label={`${n} star${n === 1 ? "" : "s"}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    className={cn(
                      "rounded-lg px-2.5 py-1.5 text-xs font-medium",
                      category === "feedback"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                    onClick={() => setCategory("feedback")}
                  >
                    Feedback
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "rounded-lg px-2.5 py-1.5 text-xs font-medium",
                      category === "feature_request"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                    onClick={() => setCategory("feature_request")}
                  >
                    Feature request
                  </button>
                </div>

                <textarea
                  className="mt-4 w-full resize-y rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                  rows={4}
                  maxLength={1000}
                  placeholder="Tell us what you like, what to improve, or a feature you want."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    {message.length}/1000
                  </span>
                  <button
                    type="button"
                    disabled={!canSubmit}
                    className={cn(
                      "rounded-xl px-3 py-2 text-sm font-semibold transition-colors",
                      canSubmit
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "cursor-not-allowed bg-muted text-muted-foreground",
                    )}
                    onClick={async () => {
                      if (!canSubmit || rating === null) return;
                      setBusy(true);
                      try {
                        await submit({
                          rating,
                          message,
                          category,
                          source: "global_widget",
                        });
                        setSubmitted(true);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {busy ? "Sending..." : "Send feedback"}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
